import { ClipboardList, Droplets, Sprout, BarChart3, ArrowUpRight } from "lucide-react";
import { MaskLine, FadeUp } from "./Reveal";
import { VINEOPS_URL } from "../constants/site";

const FEATURES = [
  [ClipboardList, "Spray & material records", "Every application logged with block, rate, and operator, audit-ready at any time."],
  [Sprout, "Phenology & crop tracking", "Budbreak to harvest, tracked against prior vintages on your ranch."],
  [Droplets, "Irrigation schedule & logs", "Sets, durations, and soil moisture readings recorded as they happen."],
  [BarChart3, "Labor, budgets & billing", "Costs by block and by task, current against the annual farming plan."],
];

const REPORT_ROWS = [
  ["Last irrigation set", "Block 7 · 6 hrs · July 14"],
  ["Degree days (GDD)", "1,842 · season to date"],
  ["Phenology", "Veraison · 78% color"],
  ["Next scheduled pass", "Canopy tuck · July 21"],
];

export const VineOps = () => (
  <section id="vineops" data-testid="vineops-section" className="bg-[#1A3626] text-[#F9F6F0] py-28 md:py-40">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-14 md:gap-10">
        <div className="md:col-span-6">
          <FadeUp>
            <img
              data-testid="vineops-logo"
              src="/assets/logo-vineops-light.png"
              alt="VineOps by Vinedresser Vineyard Management, Inc."
              className="w-60 md:w-72 h-auto mb-10"
            />
          </FadeUp>
          <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[12ch]">
            <MaskLine delay={0.1}>Every decision,</MaskLine>
            <MaskLine delay={0.22}>on record.</MaskLine>
          </h2>
          <FadeUp delay={0.35} className="mt-10 max-w-[48ch]">
            <p className="text-base md:text-lg leading-relaxed text-[#F9F6F0]/75">
              Vinedresser clients don't wait for quarterly reports. Through VineOps, your ranch data is available whenever you want it, from the field to your screen the same day.
            </p>
          </FadeUp>
          <FadeUp delay={0.5} className="mt-12">
            <a
              data-testid="vineops-login-button"
              href={VINEOPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-[#F9F6F0] text-[#12271B] text-[11px] tracking-[0.22em] uppercase font-semibold px-8 py-4 transition-colors duration-300 hover:bg-[#B89B67] active:scale-95"
            >
              <img src="/assets/vineops-mark.png" alt="VineOps" className="h-[12px] w-auto" />
              Client Login
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </FadeUp>
        </div>

        <div className="md:col-span-6">
          <FadeUp delay={0.3}>
            <div data-testid="vineops-report-card" className="border border-[#F9F6F0]/15 bg-[#12271B]">
              <div className="flex items-center justify-between px-8 py-5 border-b border-[#F9F6F0]/10">
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#F9F6F0]/60">Ranch Report</span>
                <span className="font-display italic text-[#B89B67]">Block 7 · Oakville</span>
              </div>
              <div className="px-8 py-4">
                {REPORT_ROWS.map(([label, value]) => (
                  <div key={label} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 py-4 border-b border-[#F9F6F0]/8 last:border-0">
                    <span className="text-xs tracking-[0.12em] uppercase text-[#F9F6F0]/55">{label}</span>
                    <span className="text-sm text-[#F9F6F0]/90 sm:text-right">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>

          <div className="grid sm:grid-cols-2 gap-px bg-[#F9F6F0]/10 border border-[#F9F6F0]/10 mt-8">
            {FEATURES.map(([Icon, title, body], i) => (
              <FadeUp key={title} delay={0.15 + i * 0.07} y={20}>
                <div data-testid={`vineops-feature-${i + 1}`} className="bg-[#1A3626] p-8 h-full group hover:bg-[#12271B] transition-colors duration-500">
                  <Icon size={20} className="text-[#B89B67]" strokeWidth={1.5} />
                  <h3 className="font-display text-xl mt-5 mb-2">{title}</h3>
                  <p className="text-xs leading-relaxed text-[#F9F6F0]/60">{body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
